import React, { useMemo } from "react";
import styled from "styled-components";

import {
  Card,
  SectionHeader,
  Skeleton,
} from "../../../../design-system";
import {
  IconCalendar,
  IconChart,
  IconUsers,
} from "../../../../design-system/icons/icons";
import { useAnalyticsEvents } from "../../../../database/analytics/getAnalyticsEvents";
import {
  calculateMetrics,
  getActiveUsersInDateRange,
  getNewUsersInDateRange,
} from "../../../../database/analytics/calculateMetrics";

const Stack = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
`;

const Intro = styled.div`
  max-width: 720px;

  h2 {
    margin: 0 0 var(--space-2);
    color: var(--color-text);
    font-family: var(--font-display);
    font-size: var(--font-size-xl);
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: var(--line-height-relaxed);
    text-wrap: pretty;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: var(--space-4);
`;

const StatLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const StatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: var(--color-accent-text);
  background: var(--color-accent-subtle);
  border-radius: var(--radius-sm);
`;

const StatValue = styled.div`
  margin-top: var(--space-3);
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  font-variant-numeric: tabular-nums;
`;

const StatHint = styled.div`
  margin-top: var(--space-2);
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);
`;

const StatSkeleton = styled.div`
  margin-top: var(--space-3);
`;

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProviderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: 0;
  }
`;

const ProviderName = styled.strong`
  color: var(--color-text);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  text-transform: capitalize;
`;

const ProviderCount = styled.span`
  color: var(--color-accent-text);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const EmptyCopy = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
`;

const ErrorCopy = styled.p`
  margin: 0;
  color: var(--color-danger);
`;

type StatProps = {
  label: string;
  value: number;
  hint: string;
  icon: React.ReactNode;
  loading: boolean;
};

const Stat: React.FC<StatProps> = ({
  label,
  value,
  hint,
  icon,
  loading,
}) => (
  <Card padding="md">
    <StatLabel>
      <StatIcon>{icon}</StatIcon>
      {label}
    </StatLabel>
    {loading ? (
      <StatSkeleton>
        <Skeleton width="60%" height={28} />
      </StatSkeleton>
    ) : (
      <StatValue>{value.toLocaleString()}</StatValue>
    )}
    <StatHint>{hint}</StatHint>
  </Card>
);

type Props = {
  communityId?: string;
  customerCount: number;
  customersLoading?: boolean;
};

const CustomerEngagementTab: React.FC<Props> = ({
  communityId,
  customerCount,
  customersLoading = false,
}) => {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 30);
    return { startDate: start, endDate: end };
  }, []);

  const { events, loading, error } = useAnalyticsEvents({
    communityId,
    startDate,
    endDate,
  });
  const metrics = useMemo(() => calculateMetrics(events), [events]);

  const now = useMemo(() => new Date(), []);
  const active7Start = useMemo(() => {
    const value = new Date(now);
    value.setDate(value.getDate() - 7);
    return value;
  }, [now]);
  const active24Start = useMemo(() => {
    const value = new Date(now);
    value.setHours(value.getHours() - 24);
    return value;
  }, [now]);
  const monthStart = useMemo(
    () => new Date(now.getFullYear(), now.getMonth(), 1),
    [now]
  );

  const active7 = useMemo(
    () => getActiveUsersInDateRange(events, active7Start, now),
    [active7Start, events, now]
  );
  const active24 = useMemo(
    () => getActiveUsersInDateRange(events, active24Start, now),
    [active24Start, events, now]
  );
  const newThisMonth = useMemo(
    () => getNewUsersInDateRange(events, monthStart, now),
    [events, monthStart, now]
  );
  const providers = Object.entries(metrics.signupsByProvider);

  return (
    <Stack aria-labelledby="customer-engagement-title">
      <Intro>
        <h2 id="customer-engagement-title">Customer engagement</h2>
        <p>
          Membership totals come from the customer directory. Activity and
          signup attribution use app events from the last 30 days.
        </p>
      </Intro>

      {error && <ErrorCopy>Could not load customer activity: {error}</ErrorCopy>}

      <StatGrid>
        <Stat
          label="Customers"
          value={customerCount}
          hint="Current community membership"
          icon={<IconUsers size={14} />}
          loading={customersLoading}
        />
        <Stat
          label="New users"
          value={newThisMonth}
          hint="Signed up this month"
          icon={<IconCalendar size={14} />}
          loading={loading}
        />
        <Stat
          label="Active (7d)"
          value={active7}
          hint="Unique customers in the last 7 days"
          icon={<IconChart size={14} />}
          loading={loading}
        />
        <Stat
          label="Active (24h)"
          value={active24}
          hint="Unique customers in the last day"
          icon={<IconChart size={14} />}
          loading={loading}
        />
      </StatGrid>

      <Card padding="lg">
        <SectionHeader
          title="Signup providers"
          description="How new customers created their account."
        />
        {loading ? (
          <ProviderList>
            <Skeleton width="100%" height={42} />
            <Skeleton width="100%" height={42} />
          </ProviderList>
        ) : providers.length === 0 ? (
          <EmptyCopy>No signup-provider data is available yet.</EmptyCopy>
        ) : (
          <ProviderList>
            {providers.map(([provider, count]) => (
              <ProviderRow key={provider}>
                <ProviderName>{provider}</ProviderName>
                <ProviderCount>{count.toLocaleString()}</ProviderCount>
              </ProviderRow>
            ))}
          </ProviderList>
        )}
      </Card>
    </Stack>
  );
};

export default CustomerEngagementTab;
